import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	usePaginatedAgentsByCategory,
	useRecommendedAgents,
	usePaginatedAgentsActions,
} from '@atlassian/conversation-assistant-agent/controllers/agents';
import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import { getAgentIcon } from '@atlassian/conversation-assistant-agent/utils/getDisplayedAgents';
import { MenuItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/MenuItem';
import { ChatHeaderMenuList } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/ChatHeaderMenuList';

import { messages } from './messages.ts';

// Wrapper to style the Textfield to match AgentMenu — using ref-based DOM manipulation
// to avoid @compiled/react styled limitations with child selectors
const TextfieldInputWrapper = ({ children }: { children: React.ReactNode }) => (
	<div
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
	>
		{children}
	</div>
);

// Debounce delay (ms) for server-side agent search as the user types.
const SEARCH_DEBOUNCE_MS = 400;

// Known Atlassian Studio origins allowed for the "Create agent" deep link.
// Validated in handleCreateAgent to prevent potential open redirect or XSS vectors.
const ALLOWED_STUDIO_ORIGINS = [
	'https://studio.atlassian.com',
	'https://atlassian-studio.stg-east.frontend.public.atl-paas.net',
];

const styles = cssMap({
	heading: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	loadingWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
	errorWrapper: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	searchWrapper: {
		display: 'flex',
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.075'),
		flexDirection: 'column',
		alignSelf: 'stretch',
	},
	emptySection: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
});

interface AgentSearchInputProps {
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder: string;
}

const AgentSearchInput = React.forwardRef<HTMLInputElement, AgentSearchInputProps>(
	({ value, onChange, placeholder }, ref) => (
		<Box xcss={styles.searchWrapper}>
			<TextfieldInputWrapper>
				<Textfield
					ref={ref}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					testId="confluence-agent-menu-search-input"
					elemBeforeInput={
						<Box paddingInlineStart="space.100">
							<SearchIcon label="" />
						</Box>
					}
				/>
			</TextfieldInputWrapper>
		</Box>
	),
);
AgentSearchInput.displayName = 'AgentSearchInput';

interface AgentItemProps {
	agent: Agent;
	isSelected: boolean;
	onSelect: (agent: Agent) => void;
}

const AgentItem = ({ agent, isSelected, onSelect }: AgentItemProps) => {
	const handleClick = useCallback(() => {
		onSelect(agent);
	}, [agent, onSelect]);

	return (
		<MenuItem
			label={agent.name}
			icon={getAgentIcon(agent)}
			onClick={handleClick}
			isActive={isSelected}
			isFavorite={agent.favourite}
			isVerified={agent.is_verified}
			testId={`confluence-agent-menu-agent-item-${agent.id}`}
		/>
	);
};

interface ConfluenceAgentMenuProps {
	cloudId: string;
	selectedAgentId?: string;
	onSelectAgent: (agent: Agent) => void;
	onBrowseAgents: () => void;
}

const MAX_FAVORITE_AGENTS = 5;

export const ConfluenceAgentMenu = ({
	cloudId,
	selectedAgentId,
	onSelectAgent,
	onBrowseAgents,
}: ConfluenceAgentMenuProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchInputValue, setSearchInputValue] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Focus search on mount
	useEffect(() => {
		searchInputRef.current?.focus();
	}, []);

	const assistanceServiceParams = useMemo(
		() => ({
			touchpointSource: 'confluence-object-sidebar-agents-button',
			product: 'confluence' as const,
			experienceId: 'ai-mate',
			siteId: cloudId,
			baseUrl: `/gateway/api/assist`,
		}),
		[cloudId],
	);

	// Fetch recommended agents
	const { recommendedAgents } = useRecommendedAgents({
		assistanceServiceParams,
	});

	// Fetch starred agents for favorites section
	const [{ agentsByCategory: starredAgents, isCategoryLoading: isStarredLoading }] =
		usePaginatedAgentsByCategory('starred-agents');

	// Fetch all agents for search
	const [{ agentsByCategory: allAgentsList, isCategoryLoading: isAllAgentsLoading, agentsError }] =
		usePaginatedAgentsByCategory('all-agents');

	const { fetchAgentsByCategory, populateAgentCategories } = usePaginatedAgentsActions({
		assistanceServiceParams,
	});

	// Stabilize fetchAgentsByCategory and populateAgentCategories via refs
	// to avoid stale closure issues in useEffect — same pattern used by AgentMenu.
	const fetchAgentsByCategoryRef = useRef(fetchAgentsByCategory);
	const populateAgentCategoriesRef = useRef(populateAgentCategories);
	useEffect(() => {
		fetchAgentsByCategoryRef.current = fetchAgentsByCategory;
	}, [fetchAgentsByCategory]);
	useEffect(() => {
		populateAgentCategoriesRef.current = populateAgentCategories;
	}, [populateAgentCategories]);

	// Debounced server-side search (same pattern as rovo-ai-search's CarouselComponent).
	const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
		setSearchQuery(value);
	}, SEARCH_DEBOUNCE_MS);

	// Load agents when searchQuery changes (server-side search)
	useEffect(() => {
		if (!searchQuery) {
			void fetchAgentsByCategoryRef.current({ category: 'starred-agents', query: '' });
		}
		void fetchAgentsByCategoryRef.current({ category: 'all-agents', query: searchQuery });
		return () => {
			populateAgentCategoriesRef.current({
				category: 'starred-agents',
				agentsList: [],
				pageInfo: {
					startCursor: undefined,
					endCursor: undefined,
					hasNextPage: false,
					hasPreviousPage: false,
				},
			});
			populateAgentCategoriesRef.current({
				category: 'all-agents',
				agentsList: [],
				pageInfo: {
					startCursor: undefined,
					endCursor: undefined,
					hasNextPage: false,
					hasPreviousPage: false,
				},
			});
		};
	}, [searchQuery]);

	const favoriteAgents = useMemo(() => {
		return (starredAgents ?? []).slice(0, MAX_FAVORITE_AGENTS);
	}, [starredAgents]);

	const filteredRecommendedAgents = useMemo(() => {
		// Exclude agents already in favorites
		const favoriteIds = new Set(favoriteAgents.map((a) => a.id));
		return recommendedAgents.filter((a) => !favoriteIds.has(a.id));
	}, [recommendedAgents, favoriteAgents]);

	// Search results come from server-side filtered all-agents category
	const filteredAgents = useMemo(() => {
		if (!searchQuery.trim()) {
			return null; // null = not in search mode
		}
		return allAgentsList ?? [];
	}, [searchQuery, allAgentsList]);

	const handleSelectAgent = useCallback(
		(agent: Agent) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'listItem',
					actionSubjectId: 'agent',
					source: 'confluenceObjectSidebarAgentsMenu',
					attributes: { agentId: agent.id },
				},
			}).fire();
			onSelectAgent(agent);
		},
		[onSelectAgent, createAnalyticsEvent],
	);

	const createAgentUrl = useMemo(
		() => getAtlassianStudioNLCreateUrl(cloudId, 'confluence-object-sidebar'),
		[cloudId],
	);

	const handleBrowseAgents = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgents',
				source: 'confluenceObjectSidebarAgentsMenu',
			},
		}).fire();
		onBrowseAgents();
	}, [onBrowseAgents, createAnalyticsEvent]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchInputValue(e.target.value);
			debouncedSetSearchQuery(e.target.value);
		},
		[debouncedSetSearchQuery],
	);

	const handleCreateAgent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createAgent',
				source: 'confluenceObjectSidebarAgentsMenu',
			},
		}).fire();
		// Validate URL is a known Atlassian Studio domain before opening
		// to prevent potential open redirect or XSS vectors
		try {
			const url = new URL(createAgentUrl);
			const isAllowedOrigin = ALLOWED_STUDIO_ORIGINS.some(
				(allowed) => url.origin === new URL(allowed).origin,
			);
			if (isAllowedOrigin) {
				window.open(createAgentUrl, '_blank', 'noopener,noreferrer');
			}
		} catch {
			// Invalid URL — do nothing
		}
	}, [createAgentUrl, createAnalyticsEvent]);

	const isSearchMode = filteredAgents !== null;
	const isLoading = isStarredLoading || isAllAgentsLoading;

	const searchComponent = useMemo(
		() => (
			<AgentSearchInput
				key="search"
				ref={searchInputRef}
				value={searchInputValue}
				onChange={handleSearchChange}
				placeholder={formatMessage(messages.searchPlaceholder)}
			/>
		),
		[searchInputValue, handleSearchChange, formatMessage],
	);

	const footerItems = useMemo(
		() => [
			<MenuItem
				key="browse-agents"
				label={formatMessage(messages.browseAgents)}
				icon={
					<AiAgentIcon
						label={formatMessage(messages.browseAgents)}
						color={token('color.icon.subtle')}
					/>
				}
				onClick={handleBrowseAgents}
				testId="confluence-agent-menu-browse-agents"
			/>,
			<MenuItem
				key="create-agent"
				label={formatMessage(messages.createAgent)}
				icon={
					<AddIcon label={formatMessage(messages.createAgent)} color={token('color.icon.subtle')} />
				}
				onClick={handleCreateAgent}
				isExternalLink
				testId="confluence-agent-menu-create-agent"
			/>,
		],
		[formatMessage, handleBrowseAgents, handleCreateAgent],
	);

	const favoriteItems = useMemo(
		() =>
			favoriteAgents.map((agent) => (
				<AgentItem
					key={agent.id}
					agent={agent}
					isSelected={agent.id === selectedAgentId}
					onSelect={handleSelectAgent}
				/>
			)),
		[favoriteAgents, selectedAgentId, handleSelectAgent],
	);

	const recommendedItems = useMemo(
		() =>
			filteredRecommendedAgents.map((agent) => (
				<AgentItem
					key={agent.id}
					agent={agent}
					isSelected={agent.id === selectedAgentId}
					onSelect={handleSelectAgent}
				/>
			)),
		[filteredRecommendedAgents, selectedAgentId, handleSelectAgent],
	);

	const searchResultItems = useMemo(() => {
		if (!isSearchMode) return null;
		if (filteredAgents.length === 0) {
			return [
				<Box key="empty" xcss={styles.emptySection}>
					<Text color="color.text.subtlest">{formatMessage(messages.noAgentsFound)}</Text>
				</Box>,
			];
		}
		return filteredAgents.map((agent) => (
			<AgentItem
				key={agent.id}
				agent={agent}
				isSelected={agent.id === selectedAgentId}
				onSelect={handleSelectAgent}
			/>
		));
	}, [isSearchMode, filteredAgents, selectedAgentId, handleSelectAgent, formatMessage]);

	const loadingComponent = useMemo(
		() => (
			<Box key="loading" xcss={styles.loadingWrapper}>
				<Spinner size="small" label={formatMessage(messages.loading)} />
			</Box>
		),
		[formatMessage],
	);

	const errorComponent = useMemo(
		() => (
			<Box key="error" xcss={styles.errorWrapper}>
				<Text color="color.text.subtlest">{formatMessage(messages.loadingError)}</Text>
			</Box>
		),
		[formatMessage],
	);

	const sections = useMemo(() => {
		const result = [
			{ items: [searchComponent] },
			...(agentsError
				? [{ items: [errorComponent] }]
				: isLoading
					? [{ items: [loadingComponent] }]
					: isSearchMode
						? [{ items: searchResultItems ?? [] }]
						: [
								...(favoriteItems.length > 0
									? [
											{
												title: formatMessage(messages.favoriteSection),
												items: favoriteItems,
											},
										]
									: []),
								...(recommendedItems.length > 0
									? [
											{
												title: formatMessage(messages.recommendedSection),
												items: recommendedItems,
											},
										]
									: []),
							]),
			{ items: footerItems },
		];
		return result.filter((section) => section.items.length > 0);
	}, [
		searchComponent,
		agentsError,
		errorComponent,
		isLoading,
		loadingComponent,
		isSearchMode,
		searchResultItems,
		favoriteItems,
		recommendedItems,
		footerItems,
		formatMessage,
	]);

	return (
		<>
			<Box xcss={styles.heading}>
				<Text as="span" size="small" weight="semibold" color="color.text">
					{formatMessage(messages.heading)}
				</Text>
			</Box>
			<ChatHeaderMenuList sections={sections} />
		</>
	);
};
